// Sound effects for the How to play explainer. They are Web Audio versions of
// the game's own sounds (public/game/jangle.html) so the guide sounds like a
// real match: the felt tap when a tile lands, the latch when you Play, the
// gem sparkle and the shield clank.

let ctx = null;
let noise = null;
let muted = false;

export function setExplainerMuted(v) {
  muted = !!v;
}

// Browsers only allow sound after a tap, so call this from a click handler.
export function unlockExplainerAudio() {
  try {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
  } catch {
    ctx = null;
  }
}

function audio() {
  if (muted || !ctx || ctx.state !== "running") return null;
  return ctx;
}

function tone(c, at, f0, { type = "sine", vol = 0.2, len = 0.2, f1 = null, attack = 0.012 } = {}) {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(f0, at);
  if (f1) o.frequency.exponentialRampToValueAtTime(f1, at + len * 0.8);
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(vol, at + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, at + len);
  o.connect(g);
  g.connect(c.destination);
  o.start(at);
  o.stop(at + len + 0.05);
}

function brush(c, at, freq, q, vol, len) {
  if (!noise) {
    const n = Math.floor(c.sampleRate * 0.08);
    noise = c.createBuffer(1, n, c.sampleRate);
    const d = noise.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / n, 2);
  }
  const s = c.createBufferSource();
  const bp = c.createBiquadFilter();
  const g = c.createGain();
  s.buffer = noise;
  bp.type = "bandpass";
  bp.frequency.value = freq;
  bp.Q.value = q;
  g.gain.setValueAtTime(vol, at);
  g.gain.exponentialRampToValueAtTime(0.0001, at + len);
  s.connect(bp);
  bp.connect(g);
  g.connect(c.destination);
  s.start(at);
  s.stop(at + len + 0.02);
}

const vary = (x, pct) => x * (1 + (Math.random() * 2 - 1) * pct);

const SOUNDS = {
  // Finger tap on the glass: a soft high tick.
  tap(c, t) {
    brush(c, t, 4200, 2, 0.12, 0.03);
    tone(c, t, 1800, { type: "triangle", vol: 0.05, len: 0.05, f1: 1200, attack: 0.003 });
  },
  // Direction turned: a quick rising blip.
  turn(c, t) {
    tone(c, t, 660, { type: "triangle", vol: 0.12, len: 0.12, f1: 990 });
  },
  // Tile lands on the board (the game's felt thump).
  place(c, t) {
    const f0 = vary(330, 0.12);
    tone(c, t, f0, { vol: vary(0.16, 0.2), len: 0.09, f1: f0 * 0.55, attack: 0.004 });
    brush(c, t, vary(2200, 0.2), 0.9, vary(0.06, 0.25), 0.05);
  },
  // Opponent's tile lands: same thump, a little lower and quieter.
  opp(c, t) {
    const f0 = vary(250, 0.1);
    tone(c, t, f0, { vol: 0.1, len: 0.09, f1: f0 * 0.55, attack: 0.004 });
    brush(c, t, 1600, 0.9, 0.04, 0.05);
  },
  // Backspace: a short falling pluck.
  back(c, t) {
    tone(c, t, 520, { type: "triangle", vol: 0.12, len: 0.12, f1: 300 });
  },
  // A real word: bright two-note chime.
  good(c, t) {
    tone(c, t, 784, { type: "triangle", vol: 0.16, len: 0.3 });
    tone(c, t + 0.09, 1175, { type: "triangle", vol: 0.16, len: 0.45 });
    tone(c, t + 0.09, 2350, { vol: 0.04, len: 0.4 });
  },
  // Not a word: low double buzz.
  bad(c, t) {
    tone(c, t, 196, { type: "square", vol: 0.06, len: 0.13 });
    tone(c, t + 0.16, 165, { type: "square", vol: 0.06, len: 0.18 });
  },
  // Play: the game's latch (bright click, then a solid clack).
  lock(c, t) {
    brush(c, t, 3200, 4, 0.22, 0.045);
    tone(c, t, 1900, { type: "triangle", vol: 0.05, len: 0.04, f1: 1500, attack: 0.003 });
    brush(c, t + 0.075, 1300, 3, 0.26, 0.045);
    tone(c, t + 0.075, 240, { vol: 0.28, len: 0.14, f1: 110, attack: 0.003 });
  },
  // New letters drawn: a quick marimba run, like the game's opening wave.
  draw(c, t) {
    [523, 659, 784, 1047].forEach((f, i) => {
      tone(c, t + i * 0.06, f, { type: "triangle", vol: 0.16, len: 0.2 });
    });
  },
  // "Your turn" chime from the game.
  turn2(c, t) {
    [[784, 0], [1175, 0.12]].forEach(([f, s]) => {
      tone(c, t + s, f, { type: "triangle", vol: 0.18, len: 0.45, attack: 0.015 });
      tone(c, t + s, f * 2, { vol: 0.05, len: 0.45, attack: 0.015 });
    });
  },
  // Gem collected: rising sparkle (the game's Pow pickup).
  gem(c, t) {
    [[659, 0], [880, 0.08], [1319, 0.16]].forEach(([f, s]) => {
      tone(c, t + s, f, { type: "triangle", vol: 0.18, len: s > 0.1 ? 0.45 : 0.2 });
    });
    tone(c, t + 0.2, 2637, { vol: 0.05, len: 0.3, f1: 3520 });
  },
  // Shield tile: deep thud plus a metallic clank.
  shield(c, t) {
    tone(c, t, 150, { vol: 0.3, len: 0.22, f1: 55, attack: 0.006 });
    tone(c, t, 430, { type: "square", vol: 0.03, len: 0.18 });
    tone(c, t, 1090, { type: "square", vol: 0.025, len: 0.18 });
  },
  // Pow squares pulse.
  pow(c, t) {
    tone(c, t, 110, { type: "sawtooth", vol: 0.08, len: 0.3, f1: 55 });
    brush(c, t, 900, 0.7, 0.2, 0.08);
  },
  // Pow card used: the game's dynamite (sharp crack, deep boom, rumble).
  boom(c, t) {
    brush(c, t, 2500, 0.3, 0.9, 0.09);
    tone(c, t, 90, { vol: 0.8, len: 1.1, f1: 22, attack: 0.04 });
    const n = Math.floor(c.sampleRate * 1.2);
    const buf = c.createBuffer(1, n, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource();
    const lp = c.createBiquadFilter();
    const g = c.createGain();
    src.buffer = buf;
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(500, t);
    lp.frequency.exponentialRampToValueAtTime(80, t + 1.1);
    g.gain.setValueAtTime(0.01, t);
    g.gain.linearRampToValueAtTime(0.5, t + 0.05);
    g.gain.exponentialRampToValueAtTime(0.001, t + 1.15);
    src.connect(lp);
    lp.connect(g);
    g.connect(c.destination);
    src.start(t);
    src.stop(t + 1.2);
  },
  // Raider footstep while walking the path.
  step(c, t) {
    brush(c, t, vary(700, 0.15), 1.2, 0.07, 0.04);
  },
  // Title jingle: the game's pentatonic run with a sparkle chord.
  jingle(c, t) {
    const scale = [523, 587, 659, 784, 880, 1047, 1175];
    scale.forEach((f, i) => {
      tone(c, t + i * 0.07, f, { type: "triangle", vol: 0.24, len: 0.22 });
      tone(c, t + i * 0.07, f * 2, { vol: 0.08, len: 0.22 });
    });
    const e = t + scale.length * 0.07 + 0.03;
    [1047, 1319, 1568, 2093].forEach((f, j) => tone(c, e + j * 0.018, f, { vol: 0.12, len: 0.7 }));
  },
  // Flag captured: bugle charge into a major chord.
  win(c, t) {
    [[392, 0, 0.12], [523, 0.13, 0.12], [659, 0.26, 0.12], [784, 0.39, 0.7]].forEach(([f, s, l]) => {
      tone(c, t + s, f, { type: "sawtooth", vol: 0.05, len: l });
      tone(c, t + s, f, { type: "triangle", vol: 0.16, len: l });
    });
    [523, 659, 784, 1047].forEach((f) => tone(c, t + 0.39, f, { vol: 0.07, len: 1.2 }));
  },
};

export function playExplainerSound(name) {
  const c = audio();
  const fn = SOUNDS[name];
  if (!c || !fn) return;
  try {
    fn(c, c.currentTime + 0.01);
  } catch {
    /* sound is optional */
  }
}
